import { Allocation, EnergyCompany } from "@/api/SolarCloudApi";
import { z } from "zod";
import AllocationForm from "./AllocationForm";
import { useUuid } from "@/hooks/Uuid";

const baseSchema = z.object({
  nickname: z
    .string()
    .min(1, { message: "Nickname is required" })
    .max(40, { message: "Nickname must be 40 characters or less" }),
  wattCapacity: z.number().min(1, { message: "Watt capacity is required" }),
  paymentType: z.enum(["none", "bank", "energyCompany", "paypal"]),
});

const bankSchema = baseSchema.extend({
  paymentType: z.literal("bank"),
  accountName: z
    .string()
    .min(1, { message: "Account name is required" })
    .max(40, { message: "Account name must be 40 characters or less" }),
  bsb: z.string().regex(/^\d{6}$/, { message: "BSB must be exactly 6 digits" }),
  accountNumber: z
    .string()
    .min(5, { message: "Account number must be at least 5 digits" })
    .regex(/^\d+$/, { message: "Account number must contain only numbers" })
    .max(10, { message: "Account number must be 10 digits or less" }),
});

const energyCompanySchema = baseSchema.extend({
  paymentType: z.literal("energyCompany"),
  energyCompany: z.object({
    uuid: z.string(),
    name: z.string().min(1, { message: "Please select an Energy Company" }),
  }),
  otherName: z.string().optional(),
  customerReferenceNumber: z
    .string()
    .regex(/^\d+$/, { message: "CRN must contain only numbers" })
    .min(2, { message: "CRN must be at least 2 digits" })
    .max(20, { message: "CRN must be 20 digits or less" }),
  billerCode: z
    .string()
    .regex(/^\d+$/, { message: "Biller code must contain only numbers" })
    .min(1, { message: "Biller code is required" })
    .max(10, { message: "Biller code must be 10 digits or less" }),
});

const paypalSchema = baseSchema.extend({
  paymentType: z.literal("paypal"),
  paypalEmail: z
    .string()
    .email({ message: "Please enter a valid PayPal email" }),
});

const noneSchema = baseSchema.extend({
  paymentType: z.literal("none"),
});

export const allocationFormSchema = z
  .discriminatedUnion("paymentType", [
    bankSchema,
    energyCompanySchema,
    paypalSchema,
    noneSchema,
  ])
  .refine(
    (data) => {
      if (
        data.paymentType === "energyCompany" &&
        data.energyCompany.name.toLowerCase() === "other" &&
        !data.otherName
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Energy company name is required",
      path: ["otherName"],
    }
  );

export type AllocationForm = z.infer<typeof allocationFormSchema>;

type AddAllocationFormProps = {
  energyCompanies: EnergyCompany[];
  unallocatedWatts: number;
  account: "gas" | "electricity" | "bank" | undefined;
  onSubmit: (allocation: Allocation) => void;
};

export default function AddAllocationForm({
  energyCompanies,
  unallocatedWatts,
  account,
  onSubmit,
}: AddAllocationFormProps) {
  const uuid = useUuid();
  const handleSubmit = (allocation: Allocation) => {
    onSubmit({
      ...allocation,
      uuid,
    });
  };

  return (
    <AllocationForm
      energyCompanies={energyCompanies}
      unallocatedWatts={unallocatedWatts}
      onSubmit={handleSubmit}
      account={account}
    />
  );
}
